import { useInfiniteQuery } from "@tanstack/react-query";
import fetcher from "../utils/fetcher";
import { EnumProductTypes, Listing } from "../types/listing";
import { convertServerToClientListing, sortListingsByContacts } from "./useListing";

const INFINITE_SEARCH_LISTING_QUERY_KEY = "infinite-search-listings";

export const useInfiniteSearchListingsQuery = ({
  searchQueryKey,
  searchApiQueryParams,
  pageSize,
  sortField,
  sortDir,
  searchUrl,
  productType,
}: {
  searchQueryKey: string;
  searchApiQueryParams: URLSearchParams;
  pageSize: number;
  sortField: string;
  sortDir: string;
  searchUrl: string;
  productType: EnumProductTypes;
}) => {
  return useInfiniteQuery({
    queryKey: [
      INFINITE_SEARCH_LISTING_QUERY_KEY,
      searchQueryKey,
      searchApiQueryParams.toString(),
    ],
    enabled: !!(pageSize && sortField && sortDir),
    retry: false,
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      try {
        // Create new URLSearchParams with the page parameter
        const apiParams = new URLSearchParams(searchApiQueryParams);
        apiParams.set('currentPage', pageParam.toString());
        apiParams.set('pageSize', pageSize.toString());
        apiParams.set('sortField', sortField);
        apiParams.set('sortDir', sortDir);

        const resp = await fetcher({
          url: `${searchUrl}?${apiParams.toString()}`,
        });

        const { listings, ...others } = resp.data;
        const convertedListings = resp.data.listings.reduce(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (acc: Listing[], serverListing: Record<string, any>) => {
            const convertedListing = convertServerToClientListing(
              serverListing,
              productType
            );
            if (convertedListing) {
              acc.push(convertedListing);
            }
            return acc;
          },
          []
        );

        // Secondary sort: prioritize listings with contacts within each sort group
        const sortedListings = sortListingsByContacts(convertedListings, sortField);

        return {
          listings: sortedListings,
          ...others,
        };
      } catch (error) {
        console.error("Error fetching search listings:", error);
        throw error;
      }
    },
    getNextPageParam: (lastPage, allPages) => {
      // Calculate the total number of records fetched so far
      const totalFetched = allPages.reduce((acc, page) => acc + page.listings.length, 0);

      // If we've fetched all available records, return undefined to stop pagination
      if (totalFetched >= lastPage.total_records) {
        return undefined;
      }

      // Return the next page number
      return allPages.length + 1;
    },
    getPreviousPageParam: (firstPage, allPages) => {
      return allPages.length > 1 ? allPages.length - 1 : undefined;
    },
  });
};